import { EMOTES } from './appearance';
import { WEAPON_IDS, WEAPONS } from './weapons';

export type SoundGroup = 'fire' | 'explosion' | 'world' | 'game' | 'emote';

export const SOUND_GROUPS: { id: SoundGroup; name: string }[] = [
  { id: 'fire', name: 'Výstřely' },
  { id: 'explosion', name: 'Výbuchy' },
  { id: 'world', name: 'Svět a efekty' },
  { id: 'game', name: 'Hra a rozhraní' },
  { id: 'emote', name: 'Emoty' },
];

/** Herní akce, ke které jde přiřadit zvuky z knihovny. */
export interface SoundAction {
  id: string;
  name: string;
  group: SoundGroup;
  /** Zní z místa ve světě (prostorově), jinak zepředu. */
  spatial: boolean;
  /** Hraje ve smyčce, dokud ji hra nezastaví. */
  loop?: boolean;
  /** Kolik basů poslat do subwooferu (LFE), 0–1. */
  lfe: number;
  /** Akce, jejíž zvuk se použije, když tahle žádný nemá. */
  fallback?: string;
}

const fire: SoundAction[] = [
  { id: 'fire.default', name: 'Výstřel (obecný)', group: 'fire', spatial: true, lfe: 0.4 },
  ...WEAPON_IDS.map((w): SoundAction => ({
    id: `fire.${w}`,
    name: `${WEAPONS[w].icon} ${WEAPONS[w].name}`,
    group: 'fire',
    spatial: true,
    lfe: w === 'heavy' || w === 'nuke' ? 0.7 : 0.35,
    fallback: 'fire.default',
  })),
];

export const SOUND_ACTIONS: SoundAction[] = [
  ...fire,
  { id: 'explosion.small', name: 'Výbuch – malý', group: 'explosion', spatial: true, lfe: 0.5, fallback: 'explosion.medium' },
  { id: 'explosion.medium', name: 'Výbuch – střední', group: 'explosion', spatial: true, lfe: 0.7 },
  { id: 'explosion.large', name: 'Výbuch – velký', group: 'explosion', spatial: true, lfe: 0.9, fallback: 'explosion.medium' },
  { id: 'explosion.nuke', name: 'Atomovka', group: 'explosion', spatial: true, lfe: 1, fallback: 'explosion.large' },
  { id: 'explosion.dirt', name: 'Nasypání hlíny', group: 'explosion', spatial: true, lfe: 0.4 },
  { id: 'whistle', name: 'Hvízdání padající bomby', group: 'world', spatial: true, lfe: 0 },
  { id: 'burn', name: 'Hoření napalmu', group: 'world', spatial: true, loop: true, lfe: 0.2 },
  { id: 'slide', name: 'Sesuv půdy', group: 'world', spatial: true, lfe: 0.6 },
  { id: 'splash', name: 'Šplouchnutí do vody', group: 'world', spatial: true, lfe: 0.1 },
  { id: 'teleport', name: 'Teleport', group: 'world', spatial: true, lfe: 0 },
  { id: 'hit', name: 'Zásah tanku', group: 'world', spatial: true, lfe: 0.2 },
  { id: 'death', name: 'Zničení tanku', group: 'world', spatial: true, lfe: 0.5 },
  { id: 'mine.beep', name: 'Pípnutí miny', group: 'world', spatial: true, lfe: 0 },
  { id: 'marker', name: 'Dýmovnice leteckého úderu', group: 'world', spatial: true, lfe: 0 },
  { id: 'blackhole', name: 'Černá díra', group: 'world', spatial: true, lfe: 0.8 },
  { id: 'thunder', name: 'Blesk a hrom', group: 'world', spatial: true, lfe: 0.7 },
  { id: 'quake', name: 'Zemětřesení', group: 'world', spatial: false, lfe: 1 },
  { id: 'engine', name: 'Motor řízené střely', group: 'world', spatial: true, loop: true, lfe: 0.1 },
  { id: 'drone', name: 'Rotory dronu', group: 'world', spatial: true, loop: true, lfe: 0 },
  { id: 'your_turn', name: 'Jsi na tahu', group: 'game', spatial: false, lfe: 0 },
  { id: 'win', name: 'Vítězství', group: 'game', spatial: false, lfe: 0.2 },
  { id: 'chat', name: 'Nová zpráva v chatu', group: 'game', spatial: false, lfe: 0 },
  ...EMOTES.map((e): SoundAction => ({ id: `emote.${e.id}`, name: `${e.icon} ${e.name}`, group: 'emote', spatial: true, lfe: 0 })),
];

const byId = new Map(SOUND_ACTIONS.map((a) => [a.id, a]));

export function soundAction(id: string): SoundAction | undefined {
  return byId.get(id);
}

/** Akce výbuchu podle poloměru (stejné hranice jako dřívější syntéza). */
export function explosionAction(r: number): string {
  if (r >= 80) return 'explosion.nuke';
  if (r >= 55) return 'explosion.large';
  if (r >= 30) return 'explosion.medium';
  return 'explosion.small';
}

/**
 * Řetěz akcí, ve kterém se hledá přiřazený zvuk (akce, pak její náhrady).
 * Neznámá akce výstřelu (např. „fire.pellet“) spadne na obecný výstřel.
 */
export function soundChain(id: string): string[] {
  const chain: string[] = [];
  let a = byId.get(id);
  if (!a && id.startsWith('fire.')) a = byId.get('fire.default');
  while (a && !chain.includes(a.id)) {
    chain.push(a.id);
    a = a.fallback ? byId.get(a.fallback) : undefined;
  }
  return chain;
}

// ---------- Knihovna ----------

export interface SoundInfo {
  id: string;
  name: string;
  fileName: string;
  mime: string;
  size: number;
  channels: number;
  /** Délka v sekundách. */
  duration: number;
  createdAt: number;
}

export interface SoundBinding {
  action: string;
  soundId: string;
  /** Hlasitost 0–2 (1 = beze změny). */
  volume: number;
}

export interface SoundLibraryData {
  sounds: SoundInfo[];
  bindings: SoundBinding[];
  version: number;
}

/** Největší povolený soubor (B). */
export const SOUND_MAX_BYTES = 20 * 1024 * 1024;

/** Formát zvuku podle prvních bajtů souboru (přípona ani Content-Type se nepoužívají). */
export function detectAudioFormat(b: Uint8Array): { mime: string; ext: string } | null {
  if (b.length < 12) return null;
  const ascii = (from: number, to: number) => String.fromCharCode(...b.subarray(from, to));
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WAVE') return { mime: 'audio/wav', ext: 'wav' };
  if (ascii(0, 4) === 'OggS') return { mime: 'audio/ogg', ext: 'ogg' };
  if (ascii(0, 4) === 'fLaC') return { mime: 'audio/flac', ext: 'flac' };
  if (ascii(0, 3) === 'ID3' || (b[0] === 0xff && (b[1] & 0xe0) === 0xe0)) return { mime: 'audio/mpeg', ext: 'mp3' };
  return null;
}
